import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { takeUntil } from 'rxjs';
import { SelfSignupService } from '../../services/self-signup.service';
import {
  ConnectionSelfSignup,
  ConnectionSelfSignupStatus,
  OrgType,
  ConnectionType,
  getOrgTypeLabel,
} from '../../models/connection-self-signup';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { SubscriptionService } from '../../../subscription/services/subscription.service';
import { CustomSubscriptionPlanListItem } from '../../../subscription/models/subscription';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

@Component({
  selector: 'app-manage-connection-signup',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PopupComponent],
  templateUrl: './manage-connection-signup.component.html',
  styleUrl: './manage-connection-signup.component.scss',
})
export class ManageConnectionSignupComponent
  extends DestroyBase
  implements OnInit
{
  @Input() applicationId!: number;
  @Output() closeModal = new EventEmitter<void>();
  @Output() approveClicked = new EventEmitter<void>();
  @Output() rejectClicked = new EventEmitter<void>();

  selfSignupService = inject(SelfSignupService);
  auditLogService = inject(AuditLogService);
  private subscriptionService = inject(SubscriptionService);
  private commonService = inject(CommonService);
  private formBuilder = inject(FormBuilder);

  application: ConnectionSelfSignup | null = null;
  approverNotes = '';
  isProcessing = false;
  isLoading = true;
  errorMessage = '';
  ConnectionSelfSignupStatus = ConnectionSelfSignupStatus;
  OrgType = OrgType;
  ConnectionType = ConnectionType;

  // Approval popup properties
  showApprovalPopup = false;
  approvalForm!: FormGroup;
  availablePlans: CustomSubscriptionPlanListItem[] = [];
  selectedPlanName: string | null = null;
  isLoadingPlans = false;

  // Rejection popup properties
  showRejectionPopup = false;
  rejectionReason = '';
  rejectionTouched = false;

  readonly REGION_ITEM_LIMIT = 10;
  expandedCategories: Record<string, boolean> = {
    state: false,
    county: false,
    city: false,
    zip_code: false,
  };

  ngOnInit(): void {
    this.initializeApprovalForm();
    this.loadApplicationDetails();
  }

  private initializeApprovalForm(): void {
    this.approvalForm = this.formBuilder.group({
      connectionName: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9](-[a-zA-Z0-9]|[a-zA-Z0-9])*$/),
        ],
      ],
      hasItSupport: [null, [Validators.required]],
    });
  }

  loadApplicationDetails(): void {
    this.isLoading = true;
    this.expandedCategories = {
      state: false,
      county: false,
      city: false,
      zip_code: false,
    };
    this.selfSignupService
      .getConnectionSelfSignupApplicationById(this.applicationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (app) => {
          this.application = app;
          this.approverNotes = app.approver_notes || '';
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading application:', error);
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to load application details',
          });
          this.errorMessage = 'Failed to load application details';
          this.isLoading = false;
        },
      });
  }

  toggleCategoryExpand(category: string): void {
    this.expandedCategories[category] = !this.expandedCategories[category];
  }

  isCategoryExpanded(category: string): boolean {
    return !!this.expandedCategories[category];
  }

  getVisibleItems(
    items: string[] | undefined | null,
    category: string,
  ): string[] {
    if (!items) return [];
    if (
      this.isCategoryExpanded(category) ||
      items.length <= this.REGION_ITEM_LIMIT
    ) {
      return items;
    }
    return items.slice(0, this.REGION_ITEM_LIMIT);
  }

  getStatusLabel(status?: ConnectionSelfSignupStatus): string {
    if (!status) return 'Pending';
    switch (status) {
      case ConnectionSelfSignupStatus.Pending:
        return 'Pending';
      case ConnectionSelfSignupStatus.Approved:
        return 'Approved';
      case ConnectionSelfSignupStatus.Rejected:
        return 'Rejected';
      default:
        return 'Unknown';
    }
  }

  getStatusClass(status?: ConnectionSelfSignupStatus): string {
    if (!status) return 'pending';
    switch (status) {
      case ConnectionSelfSignupStatus.Pending:
        return 'pending';
      case ConnectionSelfSignupStatus.Approved:
        return 'approved';
      case ConnectionSelfSignupStatus.Rejected:
        return 'rejected';
      default:
        return 'unknown';
    }
  }

  getOrgTypeLabel(orgType: OrgType): string {
    return getOrgTypeLabel(orgType);
  }

  approveApplication(): void {
    if (!this.application) return;

    this.isLoadingPlans = true;
    this.showApprovalPopup = true;
    this.resetApprovalForm();

    this.subscriptionService
      .getCustomsubscriptionPlans(1, 100)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.availablePlans = response.results.filter((p) => p.is_active);
          this.isLoadingPlans = false;
        },
        error: (error) => {
          console.error('Error loading subscription plans:', error);
          this.availablePlans = [];
          this.isLoadingPlans = false;
        },
      });
  }

  showRejectPopup(): void {
    if (!this.application) return;

    this.rejectionReason = '';
    this.rejectionTouched = false;
    this.showRejectionPopup = true;
  }

  private resetApprovalForm(): void {
    if (this.application) {
      this.approvalForm.patchValue({
        hasItSupport: this.application.has_it_support,
      });
    } else {
      this.approvalForm.reset();
    }
    this.selectedPlanName = null;
  }

  confirmApproval(): void {
    if (!this.application || this.approvalForm.invalid) {
      return;
    }

    this.isProcessing = true;
    const connectionName = this.approvalForm.get('connectionName')?.value;
    const hasItSupport = this.approvalForm.get('hasItSupport')?.value;

    this.auditLogService.logFeature({
      featureName: 'connection_signup',
      action: 'approve',
      meta: {
        applicationId: String(this.application.id),
        orgName: this.application.organization_name || '',
        connectionName: connectionName,
      },
      description: 'User approves connection self-signup',
    });

    this.selfSignupService
      .approveConnectionSelfSignupApplication(
        this.application.id,
        connectionName,
        this.selectedPlanName,
        hasItSupport,
        this.approverNotes,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isProcessing = false;
          this.showApprovalPopup = false;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Application approved successfully',
          });
          setTimeout(() => {
            this.closeModal.emit();
          }, 1500);
        },
        error: (error) => {
          console.error('Error approving application:', error);
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to approve application. Please try again.',
          });
          this.isProcessing = false;
        },
      });
  }

  cancelApproval(): void {
    this.showApprovalPopup = false;
    this.resetApprovalForm();
  }

  confirmRejection(): void {
    if (!this.application || !this.rejectionReason.trim()) {
      this.rejectionTouched = true;
      return;
    }

    this.isProcessing = true;

    this.auditLogService.logFeature({
      featureName: 'connection_signup',
      action: 'reject',
      meta: {
        applicationId: String(this.application.id),
        orgName: this.application.organization_name || '',
      },
      description: 'User rejects connection self-signup',
    });

    this.selfSignupService
      .rejectConnectionSelfSignupApplication(
        this.application.id,
        this.rejectionReason,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isProcessing = false;
          this.showRejectionPopup = false;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Application rejected successfully',
          });
          setTimeout(() => {
            this.closeModal.emit();
          }, 1500);
        },
        error: (error) => {
          console.error('Error rejecting application:', error);
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to reject application. Please try again.',
          });
          this.isProcessing = false;
        },
      });
  }

  cancelRejection(): void {
    this.showRejectionPopup = false;
    this.rejectionReason = '';
    this.rejectionTouched = false;
  }

  getConnectionNameError(): string {
    const control = this.approvalForm.get('connectionName');
    if (control?.hasError('required')) {
      return 'Connection name is required';
    }
    if (control?.hasError('pattern')) {
      return 'Connection name must start and end with alphanumeric characters and can contain hyphens';
    }
    return '';
  }

  ensureHttpProtocol(url: string): string {
    if (!url) return '';
    if (!/^https?:\/\//i.test(url)) {
      return 'https://' + url;
    }
    return url;
  }

  getJoinedLabel(joined?: boolean): string {
    if (joined === undefined || joined === null) return 'Unknown';
    return joined ? 'Yes' : 'No';
  }

  getJoinedClass(joined?: boolean): string {
    if (joined === undefined || joined === null) return 'unknown';
    return joined ? 'approved' : 'rejected';
  }
}
